import { useState, useCallback, useEffect, useMemo, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { PrimaryButton, ErrorBanner, Skeleton, SelectDropdown } from '@/components';
import { Sidepanel } from '@/components/layout';
import { useUser } from '@/app/providers';
import { UserProfileHeader } from '@/features/user/components/UserProfileHeader';
import { UserInfoSection, type UserFormData } from './UserInfoSection';
import { UserCompanyContactsSection } from './UserCompanyContactsSection';
import { UserInvitesSection } from './UserInvitesSection';
import { ChangePasswordModal } from './ChangePasswordModal';
import { ContactEditPanel } from '@/features/employees/components/contact/ContactEditPanel';
import { CompanyPickerDialog } from '@/features/companies/components/CompanyPickerDialog';
import { useEmployeeSidePanel } from '@/features/employee-sidepanel';
import { hasFeature } from '@/domain/authentication';
import type { Company } from '@/features/companies/types';
import {
  useUserProfile,
  useUserCompanyContacts,
  useUpdateUserProfile,
  useChangePassword,
  useWebAppLanguages,
  useSetMyLanguage,
} from '../hooks';
import { UserProfileSection } from './section';

interface UserProfileViewProps {
  userId: string;
  showCreateCompany?: boolean;
  // Enables linking this user to one of the admin's companies
  showAddToCompany?: boolean;
}

// Initialize form data from user profile - moved outside component to prevent recreation
const initializeFormData = (userProfile: any): UserFormData => ({
  username: userProfile.username,
  phoneNumber: userProfile.phoneNumber || '',
});

// Reset form data to original values - moved outside component
const resetFormData = (userProfile: any, setFormData: (data: UserFormData) => void) => {
  setFormData(initializeFormData(userProfile));
};

export function UserProfileView({ userId, showCreateCompany, showAddToCompany }: UserProfileViewProps) {
  const { t, i18n } = useTranslation();
  const { user: loggedInUser } = useUser();
  const [isEditMode, setIsEditMode] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [isCompanyPickerOpen, setIsCompanyPickerOpen] = useState(false);
  const [selectedLanguageId, setSelectedLanguageId] = useState('');
  const [selectedContact, setSelectedContact] = useState<{
    companyId: string;
    contactId: string;
  } | null>(null);

  const [formData, setFormData] = useState<UserFormData>({
    username: '',
    phoneNumber: '',
  });

  const {
    data: userProfile,
    isLoading: isProfileLoading,
    isError: isProfileError,
  } = useUserProfile(userId);

  const {
    data: companyContacts,
    isLoading: isContactsLoading,
    isError: isContactsError,
  } = useUserCompanyContacts(userId);

  const employeeSidePanel = useEmployeeSidePanel();
  const updateProfile = useUpdateUserProfile(userId);
  const changePassword = useChangePassword();
  const isOwnProfile = loggedInUser?.id === userId;
  const {
    data: webAppLanguages,
    isLoading: isLanguagesLoading,
    isError: isLanguagesError,
  } = useWebAppLanguages(isOwnProfile);
  const setMyLanguage = useSetMyLanguage();

  // Sync form data with user profile
  useEffect(() => {
    if (userProfile) {
      setFormData(initializeFormData(userProfile));
    }
  }, [userProfile]);

  const currentLanguageId = useMemo(() => {
    if (!webAppLanguages || webAppLanguages.length === 0) {
      return '';
    }

    const backendLanguage = loggedInUser?.language;
    if (backendLanguage) {
      const languageMatch = webAppLanguages.find(
        (language) =>
          String(language.id) === String(backendLanguage.id) ||
          language.isoCode === backendLanguage.isoCode
      );

      if (languageMatch) {
        return String(languageMatch.id);
      }
    }

    const activeLanguage = i18n.resolvedLanguage ?? i18n.language;
    const i18nMatch = webAppLanguages.find((language) => language.isoCode === activeLanguage);

    return i18nMatch ? String(i18nMatch.id) : '';
  }, [webAppLanguages, loggedInUser?.language, i18n.resolvedLanguage, i18n.language]);

  useEffect(() => {
    setSelectedLanguageId(currentLanguageId);
  }, [currentLanguageId]);

  // Handle form field changes
  const handleFieldChange = useCallback(
    <K extends keyof UserFormData>(field: K, value: UserFormData[K]) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      setError(null);
    },
    []
  );

  // Save profile changes
  const handleSave = useCallback(async () => {
    if (!userProfile) return;

    setError(null);

    try {
      await updateProfile.mutateAsync({
        username: formData.username,
        phoneNumber: formData.phoneNumber || undefined,
      });
      setIsEditMode(false);
    } catch {
      setError(t('userProfile.errors.updateFailed'));
    }
  }, [userProfile, formData, updateProfile, t]);

  // Enter edit mode
  const handleEditClick = useCallback(() => {
    setIsEditMode(true);
    setError(null);
  }, []);

  // Cancel edit mode and reset form
  const handleCancelEdit = useCallback(() => {
    if (userProfile) {
      resetFormData(userProfile, setFormData);
    }
    setIsEditMode(false);
    setError(null);
  }, [userProfile]);

  // Handle password change
  const handleChangePassword = useCallback(
    async (currentPassword: string, newPassword: string) => {
      await changePassword.mutateAsync({ currentPassword, newPassword });
    },
    [changePassword]
  );

  const handleLanguageChange = useCallback(
    async (event: ChangeEvent<HTMLSelectElement>) => {
      const nextLanguageId = event.target.value;
      const nextLanguage = webAppLanguages?.find(
        (language) => String(language.id) === nextLanguageId
      );

      if (!nextLanguage) {
        return;
      }

      const previousLanguageId = selectedLanguageId;

      setSelectedLanguageId(nextLanguageId);
      setError(null);

      try {
        await setMyLanguage.mutateAsync({ language: nextLanguage });
      } catch {
        setSelectedLanguageId(previousLanguageId);
        setError(t('userProfile.errors.languageUpdateFailed'));
      }
    },
    [webAppLanguages, currentLanguageId, setMyLanguage, t]
  );

  // Handle contact selection
  const handleContactClick = useCallback((companyId: string, contactId: string) => {
    setSelectedContact({ companyId, contactId });
  }, []);

  // Close contact panel
  const handleCloseContactPanel = useCallback(() => {
    setSelectedContact(null);
  }, []);

  // Close password modal
  const handleClosePasswordModal = useCallback(() => {
    setIsPasswordModalOpen(false);
  }, []);

  // Open password modal
  const handleOpenPasswordModal = useCallback(() => {
    setIsPasswordModalOpen(true);
  }, []);

  // Open the employee form of the picked company with this user already linked
  const handleCompanySelected = useCallback(
    (company: Company) => {
      if (!userProfile) return;

      employeeSidePanel.open(
        {
          type: 'create',
          linkedUser: {
            userId,
            username: userProfile.username,
            email: userProfile.email,
          },
        },
        company.id
      );
    },
    [employeeSidePanel, userId, userProfile]
  );

  const canAddToCompany =
    showAddToCompany &&
    !!loggedInUser &&
    hasFeature(loggedInUser, 'superuser.forceLinkUser');

  // Check loading and error states
  const isLoading = isProfileLoading || isContactsLoading;
  const isError = isProfileError || isContactsError;
  const canChangePassword = isOwnProfile;

  // Render loading state
  if (isLoading) {
    return (
      <div className="space-y-4 p-4 max-w-2xl mx-auto">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  // Render error state
  if (isError || !userProfile) {
    return (
      <div className="p-4 max-w-2xl mx-auto">
        <ErrorBanner hasError={true}>{t('userProfile.errors.loadFailed')}</ErrorBanner>
      </div>
    );
  }

  // Render password change button
  const renderPasswordButton = () => {
    if (!canChangePassword) return <div />;

    return (
      <button
        type="button"
        onClick={handleOpenPasswordModal}
        className="px-4 py-2 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
      >
        {t('userProfile.changePassword')}
      </button>
    );
  };

  // Render edit mode actions
  const renderEditModeActions = () => (
    <>
      {renderPasswordButton()}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={handleCancelEdit}
          className="px-4 py-2 text-sm font-medium opacity-50 hover:text-onsurface transition-colors"
        >
          {t('common.cancel')}
        </button>
        <PrimaryButton onClick={handleSave} disabled={updateProfile.isPending}>
          {updateProfile.isPending ? t('common.saving') : t('common.save')}
        </PrimaryButton>
      </div>
    </>
  );

  // Render view mode actions
  const renderViewModeActions = () => (
    <>
      {renderPasswordButton()}
      <PrimaryButton onClick={handleEditClick}>
        {t('userProfile.editProfile')}
      </PrimaryButton>
    </>
  );

  const renderLanguageField = () => {
    if (!isOwnProfile) {
      return undefined;
    }

    const placeholderLabel = isLanguagesLoading
      ? t('common.loading')
      : isLanguagesError
        ? t('userProfile.errors.languageOptionsLoadFailed')
        : t('userProfile.placeholders.language');

    return (
      <SelectDropdown
        value={selectedLanguageId}
        onChange={(event) => {
          void handleLanguageChange(event);
        }}
        disabled={isLanguagesLoading || isLanguagesError || setMyLanguage.isPending}
        aria-label={t('userProfile.fields.language')}
      >
        <option value="" disabled>
          {placeholderLabel}
        </option>
        {webAppLanguages?.map((language) => (
          <option key={language.id} value={String(language.id)}>
            {language.name}
          </option>
        ))}
      </SelectDropdown>
    );
  };

  return (
    <div className="max-w-2xl mx-auto p-4 text-onbackground">
      <UserProfileHeader
        username={userProfile.username}
        email={userProfile.email}
        registerDate={userProfile.registerDate}
        isManagedUser={!!userProfile.managedBy}
        managedByCompanyName={userProfile.managedBy?.companyName}
      />

      <div className="my-6" />

      {error && (
        <div className="mb-4 sticky">
          <ErrorBanner hasError={true}>{error}</ErrorBanner>
        </div>
      )}

      <UserInfoSection
        email={userProfile.email}
        formData={formData}
        isEditMode={isEditMode}
        onFieldChange={handleFieldChange}
        languageField={renderLanguageField()}
      />

      <div className="py-4 border-t border-border mt-4 flex justify-between items-center">
        {isEditMode ? renderEditModeActions() : renderViewModeActions()}
      </div>

      {showCreateCompany && <UserInvitesSection />}

      <UserProfileSection headline={t('userProfile.companyContacts')}>
        {canAddToCompany && (
          <div className="mb-4 flex justify-end">
            <PrimaryButton onClick={() => setIsCompanyPickerOpen(true)}>
              {t('userProfile.addToCompany')}
            </PrimaryButton>
          </div>
        )}

        <UserCompanyContactsSection
          companyContacts={companyContacts}
          loggedInUser={loggedInUser}
          showCreateCompany={showCreateCompany}
          onContactClick={handleContactClick}
        />
      </UserProfileSection>

      <CompanyPickerDialog
        isOpen={isCompanyPickerOpen}
        onClose={() => setIsCompanyPickerOpen(false)}
        onSelect={handleCompanySelected}
      />

      <ChangePasswordModal
        isOpen={isPasswordModalOpen}
        onClose={handleClosePasswordModal}
        onSubmit={handleChangePassword}
        isLoading={changePassword.isPending}
      />

      <Sidepanel
        isOpen={!!selectedContact}
        onClose={handleCloseContactPanel}
        title={t('employees.sidePanel.editTitle')}
      >
        {selectedContact && (
          <ContactEditPanel
            companyId={selectedContact.companyId}
            contactId={selectedContact.contactId}
            onClose={handleCloseContactPanel}
          />
        )}
      </Sidepanel>
    </div>
  );
}
